import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import clsx from "clsx";
import { useState } from "react";
import { useDispatch } from "react-redux";
import SRC_TYPES from "../../../dictionaries/srcTypes.json";
import {
  calculateSaleFields,
  edit as editAirSale,
  expandSale,
} from "../../../redux/sales";
import {
  formatDate,
  formatDateTime,
  getUserFullName,
  itinerarySegmentToString,
  segmentFlightDateTime,
} from "../../../utils";
import RowDetailField from "../../RowDetailField";
import SaleTypeIndicator from "../../SaleTypeIndicator";
import SaleRowContextMenu from "../SaleRowContextMenu";
import ItinerarySegmentDetails from "./ItinerarySegmentDetails";
import ItinerarySegmentSummary from "./ItinerarySegmentSummary";

const segmentKey = (saleId, s, index) => `${saleId}-${s.o}-${s.d}-${index}`;

const isEditable = (sale) => ["ER", "ES", "TR", "TS", "TX"].includes(sale.type);

const AirSaleRow = (props) => {
  const dispatch = useDispatch();

  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuTargetValue, setContextMenuTargetValue] = useState(null);

  const sale = props.sale;

  const canEdit = props.canEdit && isEditable(sale);

  const itineraryTitle = (sale.itinerary || [])
    .map(
      (segment) =>
        `${itinerarySegmentToString(segment)}${segmentFlightDateTime(segment)}`,
    )
    .join("\r");

  const isRefund = ["ER", "TR"].includes(sale.type);

  const isVoid = ["EV", "TV"].includes(sale.type);

  const isEmd = ["ER", "ES", "EV", "EX"].includes(sale.type);

  const { commission, penalty, price, serviceFee, tax, total } =
    calculateSaleFields(sale);

  const onEditSale = () => {
    dispatch(editAirSale({ id: sale.id }));
  };

  const onExpand = (event, expanded) => {
    dispatch(expandSale({ id: sale.id, expanded }));
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const showContextMenu = (targetValue) => (event) => {
    setContextMenuTargetValue(targetValue || event.target?.innerText);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX, mouseY: event.clientY }
        : null,
    );
  };

  const onContextMenu = (targetValue) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    showContextMenu(targetValue)(event);
  };

  const [touchTimeoutId, setTouchTimeoutId] = useState(null);
  const TOUCH_DURATION = 1000;

  const onTouchStart = (targetValue) => (event) => {
    setTouchTimeoutId(
      setTimeout(() => {
        showContextMenu(targetValue)(event.touches[0]);
      }, TOUCH_DURATION),
    );
  };

  const onTouchEnd = () => {
    clearTimeout(touchTimeoutId);
  };

  const onTouchMove = () => {
    clearTimeout(touchTimeoutId);
  };

  const isEdited = !!sale?._prev;

  return (
    <Accordion
      className={clsx(props.className, {
        IsEdited: isEdited,
        IsRefund: isRefund,
        IsVoid: isVoid,
      })}
      disableGutters
      elevation={0}
      expanded={sale.expanded || false}
      onChange={onExpand}
      square
      TransitionProps={{ unmountOnExit: true }}
      onContextMenu={onContextMenu()}
      onTouchStart={onTouchStart()}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SaleRowContextMenu
          contextMenu={contextMenu}
          onClose={closeContextMenu}
          targetValue={contextMenuTargetValue}
          canEdit={canEdit}
          onEdit={onEditSale}
          tkt={sale.tkt}
        />
        <Grid container xs={12} spacing={1}>
          <Grid xs={5} sm={3} md={2} lg={1.5}>
            {formatDate(sale.saleDate)}
          </Grid>
          <Grid xs={4.5} sm={4} md={2.75} lg={2.25}>
            <SaleTypeIndicator className="saleType" value={sale.type} />
            <span>{sale.pax}</span>
          </Grid>
          <Grid display={{ xs: "none", md: "block", lg: "none" }} md={1}>
            {sale.airline}
          </Grid>
          <Grid display={{ xs: "none", lg: "block" }} lg={1.75}>
            <span>{sale.airline}</span> - <span>{sale.tkt}</span>
          </Grid>
          <Grid
            display={{ xs: "none", sm: "block" }}
            title={itineraryTitle}
            sm={3}
            md={2.5}
            lg={1.5}
            onContextMenu={onContextMenu(itineraryTitle)}
            onTouchStart={onTouchStart(itineraryTitle)}
            onTouchEnd={onTouchEnd}
            onTouchMove={onTouchMove}
          >
            {(sale.itinerary || []).map((segment, index) => (
              <ItinerarySegmentSummary
                key={segmentKey(sale.id, segment, index)}
                isEmd={isEmd}
                isRefund={isRefund}
                segment={segment}
              />
            ))}
          </Grid>
          <Grid
            className="saleTotal"
            display={{ xs: "none", md: "block" }}
            md={1.25}
          >
            {price.toFormat(2)}
          </Grid>
          <Grid
            className="saleTotal"
            display={{ xs: "none", lg: "block" }}
            lg={1.25}
          >
            {serviceFee.toFormat(2)}
          </Grid>
          <Grid
            className="saleTotal"
            display={{ xs: "none", md: "block" }}
            md={1.25}
          >
            {tax.toFormat(2)}
          </Grid>
          <Grid xs={2.5} className="saleTotal" sm={2} md={1.25}>
            {total.toFormat(2)}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ paddingTop: "0px" }}>
        {sale.itinerary && (
          <Divider sx={{ margin: "0px 0px 8px 0px" }}>
            Itinerary -
            {` ${sale.numOfSegments || 0} segment${sale.numOfSegments > 1 ? "s" : ""}`}
          </Divider>
        )}
        {sale.itinerary && (
          <Grid container className="detailsContainer" xs={12} spacing={1}>
            {(sale.itinerary || []).map((s, index) => (
              <ItinerarySegmentDetails
                key={segmentKey(sale.id, s, index)}
                order={index + 1}
                isEmd={isEmd}
                isRefund={isRefund}
                segment={s}
              />
            ))}
          </Grid>
        )}
        {sale.itinerary && (
          <Divider sx={{ margin: "6px 0px" }}>Other Info</Divider>
        )}
        <Grid container className="detailsContainer" xs={12} spacing={1}>
          <RowDetailField
            display={{ md: "none" }}
            label="Airline"
            value={sale.airline}
          />
          {sale.pnr && (
            <RowDetailField
              label="PNR"
              value={sale.pnr}
              href={`pnr://${sale.pnr}`}
            />
          )}
          <RowDetailField
            label="Tkt #"
            value={sale.tkt}
            href={`tkt://${sale.tkt}`}
          />
          {sale.relTkt && (
            <RowDetailField
              label={`${sale.type === "TX" ? "Previous Tkt #" : "Related Tkt#"}`}
              value={(sale.relTkt + "").substring(0, 13)}
              href={`tkt://${(sale.relTkt + "").substring(0, 13)}`}
            />
          )}
          {sale.origTkt && (
            <RowDetailField
              label="First Tkt #"
              value={(sale.origTkt + "").substring(0, 13)}
              href={`tkt://${(sale.origTkt + "").substring(0, 13)}`}
            />
          )}
          {sale.sa && (
            <RowDetailField label="Sales Agent" value={sale.sa.name} />
          )}
          {sale.customer && (
            <RowDetailField
              label="Customer"
              value={sale?.customer?.name || ""}
            />
          )}
          {sale.sup && (
            <RowDetailField label="Supplier" value={sale?.sup?.name || ""} />
          )}
          {sale.src && (
            <RowDetailField
              label="Source"
              value={SRC_TYPES[sale.src]?.name || sale.src}
            />
          )}
          <RowDetailField label="Payment Method" value={sale.paymentMethod} />
          <RowDetailField
            display={{ md: "none" }}
            label="Price"
            value={price.toFormat(2)}
          />
          {sale.commission && (
            <RowDetailField label="Commission" value={commission.toFormat(2)} />
          )}
          <RowDetailField
            display={{ md: "none" }}
            label="Tax"
            value={tax.toFormat(2)}
          />
          {isRefund && (
            <RowDetailField label="Penalty" value={penalty.toFormat(2)} />
          )}
          {sale.serviceFee && (
            <RowDetailField
              display={{ lg: "none" }}
              label="Service Fee"
              value={serviceFee.toFormat(2)}
            />
          )}
          {sale.savedBy && (
            <RowDetailField
              label="Saved By"
              value={getUserFullName(sale.savedBy)}
            />
          )}
          <RowDetailField
            label="Saved At"
            value={formatDateTime(sale.savedAt)}
          />
          {sale.descr && (
            <RowDetailField
              label="Description"
              title={sale.descr}
              pre
              value={sale.descr}
            />
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default styled(AirSaleRow)(
  ({ theme }) => `
  &:hover {
    background-image:
      repeating-linear-gradient(90deg, ${theme.palette.secondary.main}, ${
        theme.palette.secondary.main
      } 1px, transparent 1px, transparent 2px, ${theme.palette.secondary.main} 2px),
      repeating-linear-gradient(90deg, ${theme.palette.secondary.main}, ${
        theme.palette.secondary.main
      } 1px, transparent 1px, transparent 2px, ${theme.palette.secondary.main} 2px);
    background-size: 100% 1px;
    background-position: 0 0, 0 100%;
    background-repeat: no-repeat;
  }

  &.IsEdited .MuiAccordionSummary-root {
    text-decoration: underline wavy ${theme.palette.secondary.main};
  }

  &.IsRefund .MuiAccordionSummary-root {
    color: ${theme.palette.tertiary.main};
    font-family: milliard-light;
  }

  &.IsVoid .MuiAccordionSummary-root {
    color: ${theme.palette.grey["600"]};
    font-family: milliard-light;
  }

  .MuiAccordionSummary-root {
    padding-right: 6px;
  }

  &.Mui-expanded {
    background-image:
      repeating-linear-gradient(90deg, ${theme.palette.background.paper}, ${
        theme.palette.background.paper
      } 1px, transparent 1px, transparent 2px, ${
        theme.palette.background.paper
      } 2px),
      linear-gradient(90deg, ${theme.palette.secondary.main} 30%, ${
        theme.palette.primary.main
      } 95%),
      repeating-linear-gradient(90deg, ${theme.palette.background.paper}, ${
        theme.palette.background.paper
      } 1px, transparent 1px, transparent 2px, ${
        theme.palette.background.paper
      } 2px),
      linear-gradient(90deg, ${theme.palette.secondary.main} 30%, ${
        theme.palette.primary.main
      } 95%);
    background-size: 100% 1px;
    background-position: 0 0, 0 0, 0 100%, 0 100%;
    background-repeat: no-repeat;
  }

  .MuiIconButton-root {
    padding: 0;
  }

  .MuiIconButton-root .MuiSvgIcon-root {
    font-size: 22px;
  }

  .detailsContainer {
    overflow: hidden;
  }

  .saleTotal {
    text-align: right;
  }

  .saleType {
    margin-left: -40px;
    position: absolute;
  }
`,
);
